import type { ProcessSource } from "../../api/types";
import { localDayKey, parseUtcDate } from "../../utils";

export const shouldShowProcessSourceUserName = (sources: ProcessSource[]): boolean => {
  const userNames = new Set(
    sources.map(source => source.userName?.trim()).filter((name): name is string => !!name),
  );
  return userNames.size > 1;
};

// Built once: labelling a large process formats a start time per case.
const sourceStartTimeFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/**
 * The local day a case started on, which every date filter and trend places it by. A case without
 * step timestamps (the API's minimum-instant start) sits on its recording's day, as the API does.
 */
export const caseDay = (source: Pick<ProcessSource, "traceStart" | "recordingDate">): string =>
  source.traceStart.startsWith("-") ? source.recordingDate : localDayKey(source.traceStart);

/** A case's own description, or its numbered label when it has none. */
export const runTitle = (instance: { description?: string | null }, label: string): string =>
  instance.description?.trim() || label;

export const formatProcessSourceStartTime = (source: ProcessSource): string => {
  try {
    return sourceStartTimeFormat.format(parseUtcDate(source.traceStart));
  } catch {
    return source.recordingDate;
  }
};

type DateDayFormat = "numeric" | "2-digit";

const timeLabel = (date: Date): string =>
  date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const dateLabel = (date: Date, day: DateDayFormat = "numeric"): string =>
  date.toLocaleDateString(undefined, { month: "short", day });

export const formatProcessDateRange = (startValue: string, endValue: string): string => {
  const start = parseUtcDate(startValue);
  const end = parseUtcDate(endValue);
  if (start.toDateString() === end.toDateString()) return dateLabel(start, "2-digit");
  return `${dateLabel(start, "2-digit")}-${dateLabel(end, "2-digit")}`;
};

// The RECORDED span of a group of instances — a recording's traces, or everything one user
// recorded. Resolution follows the span: inside one day it keeps clock times, and once it crosses
// days it coarsens to the two dates. Spelling both endpoints out in full ("Jun 24, 10:06 AM-Jun
// 25, 9:16 AM") is what the column cannot hold, and widening it enough to try would push the whole
// track list past the breakpoint that turns the columns on. Nothing is lost by coarsening: the
// exact times live one level down, on the instance rows this row expands into, where they are a
// single timestamp rather than a range.
export const formatProcessRecordedRange = (startValue: string, endValue: string): string => {
  const start = parseUtcDate(startValue);
  const end = parseUtcDate(endValue);
  if (start.toDateString() !== end.toDateString()) {
    return `${dateLabel(start, "2-digit")}-${dateLabel(end, "2-digit")}`;
  }
  return `${dateLabel(start, "2-digit")}, ${timeLabel(start)}-${timeLabel(end)}`;
};

type ProcessRecordingRange = {
  firstTraceStart: string;
  lastTraceEnd: string;
  rows: unknown[];
};

export const formatProcessRecordingDateRange = (
  recording: ProcessRecordingRange,
): string | null => {
  if (recording.rows.length === 0) return null;

  const start = parseUtcDate(recording.firstTraceStart);
  const end = parseUtcDate(recording.lastTraceEnd);
  const sameDay = start.toDateString() === end.toDateString();
  if (!sameDay) {
    return `${dateLabel(start)}, ${timeLabel(start)}-${dateLabel(end)}, ${timeLabel(end)}`;
  }

  if (recording.rows.length === 1) {
    return dateLabel(start);
  }

  return `${dateLabel(start)}, ${timeLabel(start)}-${timeLabel(end)}`;
};

type ProcessSourceInstanceLabelOptions = {
  source: ProcessSource;
  instanceLabel: string;
  fallbackRecordingName: string;
  showUserName: boolean;
  includeStartTime?: boolean;
};

type ProcessSourceInstanceIdentity = {
  recordingName: string;
  userName: string | null;
  instanceLabel: string;
  startTime: string;
  fullLabel: string;
};

export const processSourceInstanceIdentity = ({
  source,
  instanceLabel,
  fallbackRecordingName,
  showUserName,
  includeStartTime = true,
}: ProcessSourceInstanceLabelOptions): ProcessSourceInstanceIdentity => {
  const recordingName = source.recordingName?.trim() || fallbackRecordingName;
  const userName = showUserName ? source.userName?.trim() || null : null;
  const startTime = formatProcessSourceStartTime(source);
  const fullLabel = [userName, recordingName, instanceLabel, includeStartTime ? startTime : null]
    .filter(Boolean)
    .join(" · ");
  return {
    recordingName,
    userName,
    instanceLabel,
    startTime,
    fullLabel,
  };
};
